/**
 * Quick-Shop drawer — Figma "🎨 Quick-Shop Drawer Final" (canvas 4156:18537).
 *
 * One instance per page, rendered from layout/theme.liquid. Any element with
 * `data-quickshop-open` and `data-quickshop-url` opens it; the panel's contents
 * are fetched from /products/<handle>?view=quick-shop&section_id=quick-shop-drawer
 * so a grid of 24 cards ships one drawer instead of 24.
 *
 * Copy comes from data attributes holding already-translated strings with their
 * placeholders intact, so no wording is duplicated in JavaScript.
 *
 * `formatMoney` is a third copy of the helper in component-bundle-picker.js and
 * component-gift-threshold-bar.js. There is no module system in this theme, and
 * the alternative is editing two merged files — worth collapsing into a shared
 * asset the next time one of them is touched.
 */

/**
 * @param {number} cents - Amount in the store's minor currency unit
 * @param {string} format - shop.money_format, e.g. "$ {{amount}}"
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = Math.abs(Number(cents) || 0);

  const compose = (separator, thousands) => {
    const units = Math.floor(value / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (separator === null) {
      return units;
    }
    return `${units}${separator}${(value % 100).toString().padStart(2, '0')}`;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals':
        return compose(null, ',');
      case 'amount_with_comma_separator':
        return compose(',', '.');
      case 'amount_no_decimals_with_comma_separator':
        return compose(null, '.');
      default:
        return compose('.', ',');
    }
  });
}

/**
 * @typedef {Object} QuickShopPlan
 * @property {number} id
 * @property {string} label
 * @property {number} price
 * @property {number} save
 * @property {number} savePct
 */

/**
 * @typedef {Object} QuickShopVariant
 * @property {number} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {number} price
 * @property {number} compareAt
 * @property {string} image
 * @property {string} url
 * @property {QuickShopPlan[]} plans
 */

if (!customElements.get('quick-shop-drawer')) {
  class QuickShopDrawer extends HTMLElement {
    abortController = undefined;

    /** @type {QuickShopVariant[]} */
    variants = [];

    /** @type {(string|null)[]} */
    selection = [];

    mode = 'subscribe';

    quantity = 1;

    planId = null;

    /** @type {{productData: string, variantId: number|null}|null} */
    pendingBis = null;

    moneyFormat = '${{amount}}';

    productUrl = '';

    labels = {};

    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onPanelClick = this.onPanelClick.bind(this);
      this.onPanelChange = this.onPanelChange.bind(this);
      this.onPanelKeydown = this.onPanelKeydown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
      document.addEventListener('click', this.onTriggerClick);
      document.addEventListener('cart:change', this.onCartChange);
      this.dialog?.addEventListener('click', this.onDialogClick);
      this.dialog?.addEventListener('close', () => this.onClosed());
      this.addEventListener('click', this.onPanelClick);
      this.addEventListener('change', this.onPanelChange);
      this.addEventListener('keydown', this.onPanelKeydown);
      this.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTriggerClick);
      document.removeEventListener('cart:change', this.onCartChange);
      this.abortController?.abort();
      document.documentElement.classList.remove('qs-drawer-open');
    }

    get dialog() {
      return this.querySelector('[data-quickshop-dialog]');
    }

    get content() {
      return this.querySelector('[data-quickshop-content]');
    }

    get panel() {
      return this.querySelector('[data-quickshop-panel-root]');
    }

    /* Opening ================================================================ */

    onTriggerClick(event) {
      const trigger = event.target.closest?.('[data-quickshop-open]');
      if (!trigger) {
        return;
      }

      const url = trigger.dataset.quickshopUrl;
      if (!url) {
        return;
      }

      event.preventDefault();
      this.lastTrigger = trigger;
      this.open(url);
    }

    /**
     * @param {string} url - Product URL to render the panel from
     * @returns {Promise<void>}
     */
    async open(url) {
      this.showLoading();
      this.dialog?.showModal();
      document.documentElement.classList.add('qs-drawer-open');

      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        /* Built through URL rather than string-appended: product URLs coming
           from Shopify's recommendations API already carry `?pr_prod_strat=…`
           tracking params, and a second `?` would make the whole query string
           unparseable.

           `view=quick-shop` selects templates/product.quick-shop.json. The
           Section Rendering API 404s a section that is not in the template of
           the URL being requested, which is what made the drawer open and
           immediately close. */
        const endpoint = new URL(url, window.location.origin);
        endpoint.searchParams.set('view', 'quick-shop');
        endpoint.searchParams.set('section_id', 'quick-shop-drawer');

        const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (previewThemeId) {
          endpoint.searchParams.set('preview_theme_id', previewThemeId);
        }

        const response = await fetch(endpoint, {
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Quick shop fetch failed: ${response.status}`);
        }

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const panel = html.querySelector('[data-quickshop-panel-root]');
        if (!panel) {
          throw new Error('Quick shop response contained no panel');
        }

        this.content.replaceChildren(panel);
        this.hydrate();
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error(error);
        this.close();
      }
    }

    showLoading() {
      const loader = this.querySelector('[data-quickshop-loader]');
      if (loader) {
        this.content.replaceChildren(loader);
        return;
      }
      this.content.replaceChildren();
    }

    close() {
      this.dialog?.close();
    }

    onClosed() {
      document.documentElement.classList.remove('qs-drawer-open');
      this.abortController?.abort();

      if (this.pendingBis) {
        this.openPendingBis();
        return;
      }

      this.lastTrigger?.focus();
    }

    onDialogClick(event) {
      /* Light dismiss: `showModal()` puts the dialog in the top layer, so a
         click landing on the dialog element itself is a click on the backdrop. */
      if (event.target === this.dialog) {
        this.close();
      }
    }

    /* Hydration ============================================================== */

    hydrate() {
      const panel = this.panel;
      if (!panel) {
        return;
      }

      const dataScript = panel.querySelector('[data-quickshop-data]');
      let data = null;
      try {
        data = dataScript ? JSON.parse(dataScript.textContent) : null;
      } catch (error) {
        console.error('Quick shop data island did not parse:', error);
      }
      if (!data) {
        return;
      }

      this.variants = data.variants || [];
      this.productUrl = data.url || '';
      this.moneyFormat = data.moneyFormat || this.moneyFormat;
      this.hasSellingPlans = Boolean(data.hasSellingPlans);
      this.labels = { ...panel.dataset };
      this.quantity = 1;
      this.mode = this.hasSellingPlans ? 'subscribe' : 'otp';

      const axisCount = panel.querySelectorAll('[data-quickshop-axis]').length;

      /* Single-variant products have nothing to choose, so the one variant is
         selected up front and the CTA is live immediately. Multi-variant
         products start empty — the Default frame shows "Select flavor" behind a
         disabled CTA. */
      this.selection = this.variants.length === 1 ? [...(this.variants[0].options || [])] : new Array(axisCount).fill(null);

      this.render();

      const firstFocusable = panel.querySelector('[data-quickshop-axis-trigger], [data-quickshop-cta]');
      firstFocusable?.focus();
    }

    /* Selection ============================================================== */

    /** @returns {QuickShopVariant|null} */
    get selectedVariant() {
      if (this.selection.some((value) => value === null)) {
        return null;
      }
      return this.variants.find((variant) => this.selection.every((value, index) => variant.options[index] === value)) || null;
    }

    /**
     * Whether any variant matching this axis value could be bought. Used to mark
     * sold-out rows in the list without hiding them — the Sold-Out frame keeps
     * them selectable so the shopper can see what is unavailable.
     *
     * @param {number} axisIndex
     * @param {string} value
     * @returns {boolean}
     */
    valueAvailable(axisIndex, value) {
      return this.variants.some(
        (variant) =>
          variant.options[axisIndex] === value &&
          variant.available &&
          this.selection.every((selected, index) => index === axisIndex || selected === null || variant.options[index] === selected),
      );
    }

    onPanelClick(event) {
      const target = event.target;

      const trigger = target.closest?.('[data-quickshop-axis-trigger]');
      if (trigger) {
        this.toggleAxis(trigger.closest('[data-quickshop-axis]'));
        return;
      }

      const option = target.closest?.('[data-quickshop-axis-option]');
      if (option) {
        const axis = option.closest('[data-quickshop-axis]');
        this.selectValue(axis, option.dataset.value);
        return;
      }

      const step = target.closest?.('[data-quickshop-qty-step]');
      if (step) {
        this.setQuantity(this.quantity + Number(step.dataset.quickshopQtyStep));
        return;
      }

      if (target.closest?.('[data-quickshop-close]')) {
        this.close();
        return;
      }

      const notify = target.closest?.('[data-quickshop-cta].qs-panel__cta--notify');
      if (notify) {
        event.preventDefault();
        this.requestNotification();
        return;
      }

      /* A click anywhere else inside the panel closes any open axis list. */
      if (!target.closest?.('[data-quickshop-axis-panel]')) {
        this.closeAllAxes();
      }
    }

    onPanelChange(event) {
      const radio = event.target.closest?.('[data-quickshop-mode-radio]');
      if (radio) {
        this.mode = radio.value;
        this.render();
        return;
      }

      const planSelect = event.target.closest?.('[data-quickshop-plan]');
      if (planSelect) {
        this.planId = Number(planSelect.value) || null;
        this.render();
      }
    }

    onPanelKeydown(event) {
      if (event.key === 'Escape') {
        const openAxis = this.panel?.querySelector('[data-quickshop-axis-trigger][aria-expanded="true"]');
        if (openAxis) {
          event.stopPropagation();
          this.closeAllAxes();
          openAxis.focus();
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }

      const axis = event.target.closest?.('[data-quickshop-axis]');
      if (!axis) {
        return;
      }

      const options = [...axis.querySelectorAll('[data-quickshop-axis-option]')];
      if (!options.length) {
        return;
      }

      event.preventDefault();
      const listOpen = axis.querySelector('[data-quickshop-axis-trigger]').getAttribute('aria-expanded') === 'true';
      if (!listOpen) {
        this.toggleAxis(axis);
        options[0].focus();
        return;
      }

      const current = options.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
      options[Math.max(0, Math.min(options.length - 1, next))].focus();
    }

    toggleAxis(axis) {
      if (!axis) {
        return;
      }
      const trigger = axis.querySelector('[data-quickshop-axis-trigger]');
      const list = axis.querySelector('[data-quickshop-axis-panel]');
      const willOpen = list.hidden;

      this.closeAllAxes();
      if (!willOpen) {
        return;
      }

      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.scrollBodyToBottom();
    }

    /**
     * The axis list hangs below its trigger and is clipped by the panel body
     * on both the mobile sheet and the desktop side panel. Scroll that body
     * to the end so the open list stays in view.
     */
    scrollBodyToBottom() {
      const body = this.panel?.querySelector('[data-quickshop-body]');
      if (!body) {
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      body.scrollTo({
        top: body.scrollHeight,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    closeAllAxes() {
      this.panel?.querySelectorAll('[data-quickshop-axis]').forEach((axis) => {
        axis.querySelector('[data-quickshop-axis-panel]').hidden = true;
        axis.querySelector('[data-quickshop-axis-trigger]').setAttribute('aria-expanded', 'false');
      });
    }

    selectValue(axis, value) {
      if (!axis || value === undefined) {
        return;
      }
      const index = Number(axis.dataset.optionPosition) - 1;
      this.selection[index] = value;
      this.closeAllAxes();
      this.render();
      axis.querySelector('[data-quickshop-axis-trigger]')?.focus();
    }

    setQuantity(next) {
      this.quantity = Math.max(1, next);
      this.render();
    }

    /* Rendering ============================================================== */

    render() {
      const panel = this.panel;
      if (!panel) {
        return;
      }

      const variant = this.selectedVariant;

      this.renderAxes(panel);
      this.renderPurchaseOptions(panel, variant);
      this.renderPrices(panel, variant);
      this.renderFooter(panel, variant);
    }

    renderAxes(panel) {
      panel.querySelectorAll('[data-quickshop-axis]').forEach((axis) => {
        const index = Number(axis.dataset.optionPosition) - 1;
        const selected = this.selection[index];

        axis.querySelectorAll('[data-quickshop-axis-option]').forEach((option) => {
          const { value } = option.dataset;
          const available = this.valueAvailable(index, value);
          const isSelected = value === selected;

          const checkEl = option.querySelector('[data-quickshop-option-check]');
          const labelEl = option.querySelector('[data-quickshop-option-label]');

          if (!available) {
            checkEl.hidden = true;
            labelEl.textContent = this.labels.quickshopSoldOut || '';
          } else if (isSelected) {
            checkEl.hidden = false;
            labelEl.textContent = '';
          } else {
            checkEl.hidden = true;
            labelEl.textContent = '';
          }

          option.setAttribute('aria-selected', String(isSelected));
          option.classList.toggle('qs-axis__option--unavailable', !available);
        });

        const valueEl = axis.querySelector('[data-quickshop-axis-value]');
        const swatchEl = axis.querySelector('[data-quickshop-axis-swatch]');
        const stateEl = axis.querySelector('[data-quickshop-axis-state]');

        if (selected === null) {
          stateEl.hidden = true;
          return;
        }

        valueEl.textContent = selected;
        const chosen = axis.querySelector(`[data-quickshop-axis-option][data-value="${CSS.escape(selected)}"]`);
        const chosenSwatch = chosen?.querySelector('.qs-axis__swatch');
        swatchEl.classList.remove('qs-axis__swatch--empty');
        swatchEl.style.backgroundColor = chosenSwatch?.style.backgroundColor || '';

        const soldOut = !this.valueAvailable(index, selected);
        stateEl.hidden = !soldOut;
        stateEl.textContent = soldOut ? this.labels.quickshopSoldOut || '' : '';
      });
    }

    renderPurchaseOptions(panel, variant) {
      const purchase = panel.querySelector('[data-quickshop-purchase]');
      if (!purchase) {
        return;
      }

      purchase.querySelectorAll('[data-quickshop-mode]').forEach((option) => {
        option.classList.toggle('is-selected', option.dataset.quickshopMode === this.mode);
      });

      const reference = this.priceReference(variant);
      const plans = reference?.plans || [];

      const planSelect = purchase.querySelector('[data-quickshop-plan]');
      if (planSelect && planSelect.dataset.renderedFor !== String(reference?.id)) {
        planSelect.replaceChildren(
          ...plans.map((plan) => {
            const option = document.createElement('option');
            option.value = String(plan.id);
            option.textContent = plan.label;
            return option;
          }),
        );
        planSelect.dataset.renderedFor = String(reference?.id);
        this.planId = plans[0]?.id ?? null;
      }

      const plan = plans.find((candidate) => candidate.id === this.planId) || plans[0] || null;
      if (plan) {
        this.planId = plan.id;
      }

      const subTitle = purchase.querySelector('[data-quickshop-sub-title]');
      if (subTitle && this.labels.quickshopSubscribeLabel && plan) {
        subTitle.textContent = this.labels.quickshopSubscribeLabel.replace('{{percent}}', String(Math.round(plan.savePct)));
      }

      this.setText(purchase, '[data-quickshop-sub-price]', plan ? this.money(plan.price) : '');
      this.setText(purchase, '[data-quickshop-otp-price]', reference ? this.money(reference.price) : '');

      const subCompare = purchase.querySelector('[data-quickshop-sub-compare]');
      if (subCompare) {
        const showCompare = Boolean(plan && reference && reference.price > plan.price);
        subCompare.hidden = !showCompare;
        subCompare.textContent = showCompare ? this.money(reference.price) : '';
      }

      const subSave = purchase.querySelector('[data-quickshop-sub-save]');
      if (subSave) {
        const showSave = Boolean(plan && plan.save > 0);
        subSave.hidden = !showSave;
        subSave.textContent =
          showSave && this.labels.quickshopSaveAmount ? this.labels.quickshopSaveAmount.replace('{{amount}}', this.money(plan.save)) : '';
      }

      const perkSave = purchase.querySelector('[data-quickshop-perk-save]');
      if (perkSave && this.labels.quickshopPerkLockedIn) {
        perkSave.textContent = plan ? this.labels.quickshopPerkLockedIn.replace('{{percent}}', String(Math.round(plan.savePct))) : '';
      }
    }

    renderPrices(panel, variant) {
      const reference = this.priceReference(variant);
      if (!reference) {
        return;
      }

      /* The headline price follows the selected purchase mode, not the variant's
         list price: the Default frame shows $41.24 struck through $54.99 with
         "SAVE $13.75" while nothing is selected, and $41.24 is the subscription
         price. So the strike-through is the one-time price when subscribing,
         and the compare-at price when it is a genuine markdown. */
      const headline = this.unitPrice(variant);
      const plan = this.currentPlan(reference);
      let struck = 0;
      if (this.mode === 'subscribe' && plan) {
        struck = reference.price;
      } else if (reference.compareAt > reference.price) {
        struck = reference.compareAt;
      }
      const discounted = struck > headline;

      this.setText(panel, '[data-quickshop-price]', this.money(headline));

      const compare = panel.querySelector('[data-quickshop-compare]');
      const save = panel.querySelector('[data-quickshop-save]');

      if (compare) {
        compare.hidden = !discounted;
        compare.textContent = discounted ? this.money(struck) : '';
      }
      if (save) {
        save.hidden = !discounted;
        save.textContent =
          discounted && this.labels.quickshopSaveAmount
            ? this.labels.quickshopSaveAmount.replace('{{amount}}', this.money(struck - headline))
            : '';
      }

      const hero = panel.querySelector('[data-quickshop-hero]');
      if (hero && variant?.image) {
        hero.src = variant.image;
      }
    }

    renderFooter(panel, variant) {
      const details = panel.querySelector('[data-quickshop-details]');
      if (details) {
        details.href = variant?.url || this.productUrl || details.getAttribute('href');
      }

      this.setText(panel, '[data-quickshop-qty-value]', String(this.quantity));

      const minus = panel.querySelector('[data-quickshop-qty-step="-1"]');
      if (minus) {
        minus.disabled = this.quantity <= 1;
      }

      const unit = this.unitPrice(variant);
      this.setText(panel, '[data-quickshop-total]', this.money(unit * this.quantity));

      const cta = panel.querySelector('[data-quickshop-cta]');
      const ctaText = panel.querySelector('[data-quickshop-cta-text]');
      if (!cta || !ctaText) {
        return;
      }

      const variantInput = panel.querySelector('[data-quickshop-variant-input]');
      const quantityInput = panel.querySelector('[data-quickshop-quantity-input]');
      const planInput = panel.querySelector('[data-quickshop-plan-input]');

      if (variantInput) {
        variantInput.value = variant ? String(variant.id) : '';
      }
      if (quantityInput) {
        quantityInput.value = String(this.quantity);
      }
      if (planInput) {
        const isSubscribe = this.mode === 'subscribe';
        const usePlan = Boolean(isSubscribe && this.planId);
        planInput.disabled = !usePlan;
        planInput.value = usePlan ? String(this.planId) : '';
      }

      cta.classList.remove('qs-panel__cta--notify');

      if (!variant) {
        cta.type = 'submit';
        cta.disabled = true;
        ctaText.textContent = this.labels.quickshopSelectLabel || ctaText.textContent;
        return;
      }

      if (!variant.available) {
        /* Amp Back in Stock owns the email form. The CTA closes this drawer
           and clicks the collection-style `.BIS_trigger` — `BIS.popup.show()`
           only works on product pages. */
        cta.type = 'button';
        cta.disabled = false;
        cta.classList.add('qs-panel__cta--notify');
        ctaText.textContent = this.labels.quickshopNotifyLabel || '';
        return;
      }

      cta.type = 'submit';
      cta.disabled = false;

      const template = this.mode === 'subscribe' && this.planId ? this.labels.quickshopAddSubscription : this.labels.quickshopAddToCart;

      ctaText.textContent = (template || '').replace('{{price}}', this.money(unit * this.quantity));
    }

    /**
     * @param {QuickShopVariant|null} variant
     * @returns {QuickShopVariant|null} The variant to price from — the selected
     *   one, or the first buyable one while nothing is selected yet
     */
    priceReference(variant) {
      return variant || this.variants.find((candidate) => candidate.available) || this.variants[0] || null;
    }

    /**
     * @param {QuickShopVariant|null} reference
     * @returns {QuickShopPlan|null} The selling plan currently chosen
     */
    currentPlan(reference) {
      if (!reference) {
        return null;
      }
      return (reference.plans || []).find((candidate) => candidate.id === this.planId) || null;
    }

    /**
     * @param {QuickShopVariant|null} variant
     * @returns {number} Unit price in cents for the selected purchase mode
     */
    unitPrice(variant) {
      const reference = this.priceReference(variant);
      if (!reference) {
        return 0;
      }

      if (this.mode === 'subscribe') {
        const plan = this.currentPlan(reference);
        if (plan) {
          return plan.price;
        }
      }
      return reference.price;
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    setText(root, selector, text) {
      const el = root.querySelector(selector);
      if (el) {
        el.textContent = text;
      }
    }

    /* Cart =================================================================== */

    onSubmit(event) {
      const form = event.target.closest?.('[data-quickshop-form]');
      if (!form) {
        return;
      }

      event.preventDefault();
      // cart.js also listens for /cart/add submits and would queue a second
      // add. The first one lands, then that queued one bumps the quantity.
      event.stopPropagation();
      if (!this.selectedVariant?.available) {
        return;
      }

      const cta = form.querySelector('[data-quickshop-cta]');
      const spinner = cta?.querySelector('.loading__spinner');
      cta?.setAttribute('aria-busy', 'true');
      spinner?.classList.remove('hidden');

      window.Cart?.add(new FormData(form), cta)
        .catch((error) => {
          console.warn('Quick shop add to cart failed:', error.message);
        })
        .finally(() => {
          cta?.removeAttribute('aria-busy');
          spinner?.classList.add('hidden');
        });

      // The cart drawer opens on this same submit, in its capture listener, and
      // shows its loading skeleton while the add runs. This dialog is in the top
      // layer, so left open until cart:change it covers that skeleton for the
      // whole request. Hand the screen over now; the trigger is cleared so
      // onClosed() doesn't pull focus out of the cart drawer.
      this.lastTrigger = null;
      this.close();
    }

    /**
     * The cart drawer opens itself on submit and again on `cart:change` with
     * action 'add' (assets/component-cart-drawer.js). This drawer sits in the
     * top layer, so it would paint over that panel — closing is what hands the
     * screen over, not stacking order. onSubmit() closes it as the add starts;
     * this covers an add that reaches the cart without going through that form.
     */
    onCartChange(event) {
      if (event.detail?.action !== 'add') {
        return;
      }
      if (!this.dialog?.open) {
        return;
      }
      this.close();
    }

    /**
     * Amp's `BIS.popup.show()` is product-page only. Collection-style signup
     * needs a `.BIS_trigger` with `data-product-data`. The drawer is a
     * `showModal()` dialog, so it has to close before that popup can receive
     * clicks — the trigger lives outside the dialog for the same reason.
     */
    requestNotification() {
      const variant = this.selectedVariant;
      const productData = this.panel?.querySelector('[data-quickshop-product]')?.textContent?.trim() || '';

      this.dispatchEvent(
        new CustomEvent('quick-shop:notify', {
          bubbles: true,
          detail: { variantId: variant?.id ?? null },
        }),
      );

      this.pendingBis = {
        productData,
        variantId: variant?.id ?? null,
      };
      this.close();
    }

    openPendingBis() {
      const pending = this.pendingBis;
      this.pendingBis = null;
      if (!pending) {
        return;
      }

      if (!pending.productData) {
        console.warn('Quick shop: no back-in-stock product data.');
        return;
      }

      const trigger = this.querySelector('[data-quickshop-bis-trigger]');
      if (!trigger) {
        console.warn('Quick shop: no back-in-stock trigger on this page.');
        return;
      }

      trigger.setAttribute('data-product-data', pending.productData);
      if (pending.variantId) {
        trigger.setAttribute('data-variant-id', String(pending.variantId));
      } else {
        trigger.removeAttribute('data-variant-id');
      }

      trigger.click();
    }
  }

  customElements.define('quick-shop-drawer', QuickShopDrawer);
}
